import {CoreSettingsParameters, DeviceType, ISettings, SettingsParametersSchema} from '@gongxigames/framework/core';
import {WinTypeConfig} from '@gongxigames/framework/slot/modules/winpresentation';
import {PromotionIntegrationType} from '@gongxigames/framework/promotions';
import {IntegrationType} from '@gongxigames/framework/slot/types';
import {RegulationTypes} from '@gongxigames/framework/slot/services/logic';

export type SlotSettings = ISettings<CoreSettingsParameters & SlotSettingsParameters>;

export const SLOT_SETTINGS_PARAMETERS_SCHEMA: SettingsParametersSchema<SlotSettingsParameters> = {
    skipHardwareAccelerationDisabledMessage: {
        defaultValue: false
    },
    cdn: {
        readonly: true,
        defaultValue: ''
    },
    root: {
        readonly: true,
        defaultValue: ''
    },
    sessionsURL: {
        readonly: true,
        defaultValue: ''
    },
    quickSpin: {
        defaultValue: false,
        keepInStorage: true
    },
    skin: {
        readonly: true,
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    turboModeDisabled: {
        readonly: true,
        defaultValue: false
    },
    quickStopDisabled: {
        readonly: true,
        defaultValue: false
    },
    fullscreenDisabled: {
        readonly: true,
        defaultValue: false
    },
    minBet: {
        readonly: true,
        defaultValue: 0
    },
    maxBet: {
        readonly: true,
        defaultValue: 0
    },
    soundsMuted: {
        keepInStorage: true,
        defaultValue: false
    },
    // 1. Sound regulated using url parameter 'sound'
    // a) default behavior - if no parameter specified (or its value cannot be parsed as number),
    // sound is enabled and icon should be visible.
    // b) parameter value may be 0, positive (0...1) or negative (positive value is volume level)
    // c) if parameter value is 0 or positive, sound and/or music control(s) should be available for user,
    // otherwise not. For example, if parameter is 0, sound muted at start (volume 0)
    // d) if game doesn't contain any sound, naturally, sound control shouldn't be available
    soundVolume: {
        defaultValue: 0.6,
        keepInStorage: true
    },
    soundEnabled: {
        readonly: true,
        defaultValue: true
    },
    gameRulesRealityCheckDisabled: {
        readonly: true,
        defaultValue: false
    },
    gameRulesSessionsLifeDisabled: {
        readonly: true,
        defaultValue: false
    },
    autoSpinDisabled: {
        readonly: true,
        defaultValue: false
    },
    allAutoplayLimitsDisabled: {
        readonly: true,
        defaultValue: false
    },
    autoplayStopWhenAnyBonus: {
        defaultValue: false
    },
    autoplayShowBetAndWager: {
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    autoplayShowConfirmationPopup: {
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    autoplayMode: {
        defaultValue: false
    },
    autoplaySkipBigWins: {
        defaultValue: false
    },
    autoplaySkipLongSpins: {
        defaultValue: false
    },
    autoplaySkipWinAnimation: {
        defaultValue: false
    },
    autoplayRequiredSingleWin: {
        readonly: true,
        defaultValue: false
    },
    autoplayRequiredLossLimit: {
        readonly: true,
        defaultValue: false
    },
    autoplayRequiredWinLimit: {
        readonly: true,
        defaultValue: false
    },
    autoplayDefaultSelectedSpins: {
        defaultValue: 2
    },
    autoplayValues: {
        readonly: true,
        defaultValue: [10, 20, 50, 75, 100]
    },
    showBet: {
        readonly: true,
        defaultValue: true
    },
    showBalance: {
        readonly: true,
        defaultValue: true
    },
    historyUrl: {
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    supportUrl: {
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    logoutUrl: {
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    cashierUrl: {
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    homeUrl: {
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string',
            parseValue: value => value.startsWith('http://') ? 'https://' + value.slice('http://'.length) : value
        }
    },
    exitUrl: {
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    currency: {
        defaultValue: 'EUR',
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    offlineMode: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            alias: 'mode',
            typePropertyInURL: 'string',
            parseValue: value => value && (value === 'offline' || value === 'demo')
        }
    },
    funMode: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            alias: 'mode',
            typePropertyInURL: 'string',
            parseValue: value => {
                return value && value === 'fun';
            }
        }
    },
    funModeBalance: {
        readonly: true,
        defaultValue: 0,
        urlParser: {
            typePropertyInURL: 'number'
        }
    },
    specialWinTypeConfig: {
        defaultValue: undefined
    },
    showWinProbability: {
        defaultValue: false
    },
    showMaxWinProbabilityOnUI: {
        defaultValue: false
    },
    showMaxMultiplier: {
        readonly: true,
        defaultValue: false
    },
    showRTPWatermark: {
        readonly: true,
        defaultValue: false
    },
    inactivityTimer: {
        defaultValue: 0,
        readonly: true,
        urlParser: {
            typePropertyInURL: 'number',
            debugOnly: true
        }
    },
    hideJackpotWords: {
        defaultValue: false
    },
    hideHomeButtonDescription: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    hideHomeButton: {
        readonly: true,
        defaultValue: false
    },
    hideSoundCheckBox: {
        readonly: true,
        defaultValue: false
    },
    hideLogoutButton: {
        readonly: true,
        defaultValue: false
    },
    hideCashierButton: {
        readonly: true,
        defaultValue: false
    },
    hideSupportButton: {
        readonly: true,
        defaultValue: false
    },
    hideHistoryButton: {
        readonly: true,
        defaultValue: false
    },
    hideGameRulesButton: {
        readonly: true,
        defaultValue: false
    },
    minMaxBetVisible: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    clock: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            alias: 'clock',
            typePropertyInURL: 'boolean'
        }
    },
    integrationType: {
        readonly: true,
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    enableDetailedRTP: {
        readonly: true,
        defaultValue: false
    },
    enableRTPExplanation: {
        readonly: true,
        defaultValue: false
    },
    enableRTPExplanation2: {
        readonly: true,
        defaultValue: false
    },
    hideRTP: {
        readonly: true,
        defaultValue: false
    },
    enableTriggeringJackpotsExplanation: {
        readonly: true,
        defaultValue: false
    },
    // debug parameters
    rewindVisible: {
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    // end debug parameters
    // TODO refactor rename
    ukRegulations: {
        readonly: true,
        defaultValue: false
    },
    realityCheckTimeout: {
        readonly: true,
        defaultValue: 0,
        urlParser: {
            typePropertyInURL: 'number',
            parseValue: value => value * 1000 * 60
        }
    },
    realityCheckTimeoutPassed: {
        readonly: true,
        defaultValue: 0,
        urlParser: {
            typePropertyInURL: 'number',
            parseValue: value => value * 1000 * 60
        }
    },
    roundDelay: {
        readonly: true,
        defaultValue: 0
    },
    defaultStake: {
        defaultValue: -1
    },
    stakes: {
        defaultValue: [],
        readonly: true
    },
    bets: {
        defaultValue: [],
        readonly: true
    },
    debugger: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    device: {
        readonly: true,
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    override: {
        readonly: true,
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    enableSpecialWinType: {
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    imageFormat: {
        defaultValue: undefined,
        urlParser: {
            typePropertyInURL: 'string'
        }
    },
    gameHistoryListEnabled: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    showOddsUpfront: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    netPositionEnabled: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    elapsedTimeEnabled: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    sessionEarningsEnabled: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    sessionBetsEnabled: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    regulationType: {
        readonly: true,
        defaultValue: undefined
    },
    changeWinToPayout: {
        readonly: true,
        defaultValue: false
    },
    buyFeatureAvailable: {
        readonly: true,
        defaultValue: false
    },
    maxBuyFeatureBet: {
        readonly: true,
        defaultValue: Number.MAX_VALUE
    },
    additionalInformationDisabled: {
        readonly: true,
        defaultValue: false
    },
    gameTerminologyDisabled: {
        readonly: true,
        defaultValue: false
    },
    disconnectionSectionDisabled: {
        readonly: true,
        defaultValue: false
    },
    multiplierSignFirst: {
        readonly: true,
        defaultValue: false
    },
    gameRulesRealityCheckUKGCStringDisabled: {
        readonly: true,
        defaultValue: false
    },
    nonMinimalBetPopupEnabled: {
        readonly: true,
        defaultValue: false
    },
    gameRulesAdditionalDataAboutLinesWaysEnable: {
        readonly: true,
        defaultValue: false
    },
    isPointsCurrencyEnabled: {
        readonly: true,
        defaultValue: false
    },
    freeRoundsValuedAtEnabled: {
        readonly: true,
        defaultValue: true
    },
    freeRoundsExpiresInEnabled: {
        readonly: true,
        defaultValue: false
    },
    freeRoundsYouWonEnabled: {
        readonly: true,
        defaultValue: true
    },
    freeRoundsOptOutEnabled: {
        readonly: true,
        defaultValue: false
    },
    freeRoundsPlayLaterEnabled: {
        readonly: true,
        defaultValue: true
    },
    freeRoundsOptOutConfirmationPopupEnabled: {
        readonly: true,
        defaultValue: true
    },
    awardedFeatureValuedAtEnabled: {
        readonly: true,
        defaultValue: true
    },
    awardedFeatureExpiresInEnabled: {
        readonly: true,
        defaultValue: false
    },
    awardedFeatureYouWonEnabled: {
        readonly: true,
        defaultValue: true
    },
    awardedFeatureOptOutEnabled: {
        readonly: true,
        defaultValue: false
    },
    awardedFeaturePlayLaterEnabled: {
        readonly: true,
        defaultValue: true
    },
    searchInfoFeatureEnabled: {
        readonly: true,
        defaultValue: false
    },
    awardedFeatureOptOutConfirmationPopupEnabled: {
        readonly: true,
        defaultValue: true
    },
    promotionIntegrationImplementation: {
        readonly: true,
        defaultValue: 'Default'
    },
    hideBetPanelDuringAwardedFeature: {
        readonly: true,
        defaultValue: true,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
    freezeNewTextsUpdate: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    enablePersistentFeatureExplanation: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    enableOpenPageInCurrentIFrame: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    enableOpenInSameTabDepositPage: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean',
            debugOnly: true
        }
    },
    hideDemoMark: {
        readonly: true,
        defaultValue: false,
        urlParser: {
            typePropertyInURL: 'boolean'
        }
    },
};

export interface SlotSettingsParameters {
    skipHardwareAccelerationDisabledMessage: boolean;
    cdn: string;
    root: string;
    sessionsURL: string;
    quickSpin: boolean;
    skin?: string;
    turboModeDisabled: boolean;
    quickStopDisabled: boolean;
    fullscreenDisabled?: boolean;

    minBet: number;
    maxBet: number;

    soundsMuted: boolean;
    soundEnabled: boolean;
    soundVolume: number;

    gameRulesRealityCheckDisabled: boolean;
    gameRulesSessionsLifeDisabled: boolean;
    autoSpinDisabled: boolean;
    allAutoplayLimitsDisabled: boolean;
    autoplayStopWhenAnyBonus: boolean;
    autoplayShowBetAndWager: boolean;
    autoplayShowConfirmationPopup: boolean;
    autoplayMode: boolean;

    autoplaySkipBigWins: boolean;
    autoplaySkipLongSpins: boolean;
    autoplaySkipWinAnimation: boolean;
    autoplayRequiredSingleWin: boolean;
    autoplayRequiredLossLimit: boolean;
    autoplayRequiredWinLimit: boolean;
    autoplayDefaultSelectedSpins: number;
    autoplayValues: number[];

    showBet: boolean;
    showBalance: boolean;
    historyUrl?: string;
    supportUrl?: string;
    logoutUrl?: string;
    cashierUrl?: string;
    homeUrl?: string;
    exitUrl?: string;
    currency: string;
    offlineMode: boolean;
    funMode: boolean;
    funModeBalance: number;

    specialWinTypeConfig?: WinTypeConfig;
    showWinProbability: boolean;
    showMaxWinProbabilityOnUI: boolean;
    showMaxMultiplier: boolean;
    showRTPWatermark: boolean;

    inactivityTimer: number;
    hideJackpotWords: boolean;
    hideHomeButtonDescription: boolean;

    hideHomeButton: boolean;
    hideSoundCheckBox: boolean;
    hideGameRulesButton: boolean;
    hideLogoutButton: boolean;
    hideHistoryButton: boolean;
    hideSupportButton: boolean;
    hideCashierButton: boolean;

    minMaxBetVisible: boolean;
    clock: boolean;
    // TODO: refactor to PromotionType
    integrationType?: IntegrationType;

    enableDetailedRTP: boolean;
    enableRTPExplanation: boolean;
    enableRTPExplanation2: boolean;
    hideRTP: boolean;
    enableTriggeringJackpotsExplanation: boolean;
    rewindVisible: boolean;
    ukRegulations: boolean;
    realityCheckTimeout: number;
    realityCheckTimeoutPassed: number;
    roundDelay: number;

    disableFullscreen?: boolean;
    defaultStake?: number;
    stakes?: number[];
    bets: number[];
    debugger?: boolean;
    device?: DeviceType;
    imageFormat?: string;
    override?: string;
    enableSpecialWinType?: boolean;
    gameHistoryListEnabled?: boolean;
    showOddsUpfront?: boolean;
    netPositionEnabled: boolean;
    sessionEarningsEnabled: boolean;
    sessionBetsEnabled: boolean;
    elapsedTimeEnabled: boolean;
    regulationType?: RegulationTypes;
    changeWinToPayout: boolean;
    buyFeatureAvailable?: boolean;
    maxBuyFeatureBet?: number;
    nonMinimalBetPopupEnabled: boolean;
    freeRoundsYouWonEnabled: boolean;
    freeRoundsValuedAtEnabled: boolean;
    freeRoundsExpiresInEnabled: boolean;
    freeRoundsOptOutEnabled: boolean;
    freeRoundsPlayLaterEnabled: boolean;
    freeRoundsOptOutConfirmationPopupEnabled: boolean;
    awardedFeatureYouWonEnabled: boolean;
    awardedFeatureValuedAtEnabled: boolean;
    awardedFeatureExpiresInEnabled: boolean;
    awardedFeatureOptOutEnabled: boolean;
    awardedFeaturePlayLaterEnabled: boolean;
    searchInfoFeatureEnabled: boolean;
    awardedFeatureOptOutConfirmationPopupEnabled: boolean;
    promotionIntegrationImplementation: PromotionIntegrationType;
    hideBetPanelDuringAwardedFeature: boolean;
    additionalInformationDisabled: boolean;
    gameTerminologyDisabled: boolean;
    disconnectionSectionDisabled: boolean;
    multiplierSignFirst: boolean;
    gameRulesRealityCheckUKGCStringDisabled: boolean;
    isPointsCurrencyEnabled?: boolean;
    gameRulesAdditionalDataAboutLinesWaysEnable: boolean;
    /**
     * disable sentry error reporting by configuration provider
     */
    disableClientErrorReporting?: boolean;
    /**
     * Mostly for NJ market, prevents texts update, to not go into new certification cycle.
     */
    freezeNewTextsUpdate?: boolean;
    /**
     * Enables visibility of "Persistent Feature Explanation" text in game rules popup only for social casinos (en_soc).
     */
    enablePersistentFeatureExplanation?: boolean;
    /**
     * Enables redirect by the link (for e.g. homeUrl, cashierUrl etc.) in current iframe instead of default 'top' window.
     */
    enableOpenPageInCurrentIFrame?: boolean;
    /**
     * Enables redirect by the deposit link in the same tab as game was opened.
     */
    enableOpenInSameTabDepositPage?: boolean;
    /**
     * This parameter disables the Demo Mode indicator if it's true
     */
    hideDemoMark?: boolean;
}
